import format from 'string-template';

import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';
import {
  uploadMessageAttachments,
  getProcessedAttachmentData,
  cleanAttachmentTitle,
} from '../../../../../utils/attachments';
import { MAX_FILE_UPLOAD_SIZE } from '../../../../../utils/files';
import { ANNOUNCEMENT_USER_TYPE_TAG_NAMES } from '../../../../Model/Announcement';
import { GENERIC_COMMUNITY, CommunityType, isGenericCommunityId } from '../../../../Model/Community';

// 1:1 Coaching generic community id (pod posts, all-pods).
const ONE_ON_ONE_ID = ANNOUNCEMENT_USER_TYPE_TAG_NAMES.OTHER.value;
// Default community for new posts / not-found fallback — same value, distinct intent from ONE_ON_ONE_ID.
const DEFAULT_COMMUNITY_ID = ONE_ON_ONE_ID;

const DELIVERY_MODE = {
  POST_NOW: 'POST_NOW',
  SCHEDULE: 'SCHEDULE',
};

/**
 * Process the attachments for the announcement.
 *
 * @param {Object[]} fileAttachments - The files to process.
 * @param {Object} [audioAttachment] - The audio attachment to process.
 * @param {string} coachId - The coach id.
 * @param {string} loggedInUserId - The logged in user's id.
 * @param {Function} uploadAttachments - The function to upload the attachments.
 * @param {Function} deleteAttachments - The function to delete the attachments.
 * @returns {Promise<{attachments: Object[], existingAttachments: Object[], error: boolean}>}
 */
const processAttachments = async ({
  fileAttachments,
  audioAttachment,
  coachId,
  loggedInUserId,
  uploadAttachments,
  deleteAttachments,
}) => {
  const attachments = [];

  // Separate existing attachments from new ones
  const existingAttachments = [];
  const newAttachments = [];

  fileAttachments.forEach((file) => {
    // If the file has a url, it is an existing attachment. We just need to get the processed attachment data.
    if (file.url) {
      attachments.push(getProcessedAttachmentData({
        fileName: file.name,
        pathRef: file.url,
        contentType: file.type,
        fileSize: file.size,
      }));
      // If an error occurs while uploading the new attachments, we need to restore the existing attachments.
      existingAttachments.push(file);
    } else {
      // If not, it is a new attachment. We need to upload it.
      newAttachments.push(file);
    }
  });

  // If there is an audio attachment, we need to upload it.
  if (audioAttachment) {
    newAttachments.push(audioAttachment);
  }

  if (newAttachments.length) {
    const storagePath = format(storagePaths.COACH_ANNOUNCEMENTS_ATTACHMENTS, {
      [pathPlaceholder.COACH_ID]: coachId,
    });

    const { storageTaskFailed, attachmentsData } = await uploadMessageAttachments({
      fileAttachments: newAttachments,
      loggedInUserId,
      uploadAttachments,
      deleteAttachments,
      storagePath,
    });

    if (storageTaskFailed) {
      return { error: true, attachments: existingAttachments };
    }

    // Clean the title of the attachments from the UUID
    attachmentsData.forEach((attachment) => {
      const title = cleanAttachmentTitle(attachment.title);
      attachments.push({
        ...attachment,
        title,
      });
    });
  }
  return { attachments, error: false };
};

const onFilesSelected = (event) => {
  const { files } = event.target;
  if (files.length) {
    const filesArray = [...files];
    const validFiles = filesArray.every((file) => file.size < MAX_FILE_UPLOAD_SIZE);
    if (validFiles) {
      return filesArray;
    }
    return [];
  }
  return [];
};

/**
 * Turns the selected community into the fields a post writes: the new communityId/communityName,
 * plus the legacy tag/pod fields so old apps still see the post. `isPodEnabled` decides whether an
 * all-pods 1:1 post gets podId='' (pod coaches) or no podId (non-pod coaches).
 */
const buildPostFieldsForCommunity = (selectedId, communities, isPodEnabled) => {
  if (isGenericCommunityId(selectedId)) {
    const generic = GENERIC_COMMUNITY.find((option) => option.id === selectedId);
    return {
      communityId: selectedId,
      communityName: generic?.name || '',
      tagId: selectedId,
      tagName: selectedId,
      podId: (selectedId === ONE_ON_ONE_ID && isPodEnabled) ? '' : null,
    };
  }

  const community = communities.find((item) => item.id === selectedId);
  if (!community) {
    const fallback = GENERIC_COMMUNITY.find((option) => option.id === DEFAULT_COMMUNITY_ID);
    return {
      communityId: DEFAULT_COMMUNITY_ID,
      communityName: fallback?.name || '',
      tagId: DEFAULT_COMMUNITY_ID,
      tagName: DEFAULT_COMMUNITY_ID,
      podId: isPodEnabled ? '' : null,
    };
  }

  if (community.type === CommunityType.POD) {
    return {
      communityId: community.id,
      communityName: community.name,
      tagId: ONE_ON_ONE_ID,
      tagName: ONE_ON_ONE_ID,
      podId: community.podId,
    };
  }

  // CUSTOM: old apps read one tag per post, so write the first tag for compatibility.
  // Name-only community (no tags) → null, so the legacy tagId/tagName are skipped.
  const primaryTag = community.customTags[0] || null;
  return {
    communityId: community.id,
    communityName: community.name,
    tagId: primaryTag,
    tagName: primaryTag,
    podId: primaryTag && isPodEnabled ? '' : null,
  };
};

/**
 * The community a post currently belongs to, as an option id the recipients field can preselect.
 * New posts store `communityId` directly. Older ones do not, so fall back to their pod (its pod
 * community), then their tag (the matching generic), and finally the default.
 *
 * @param {Object} announcement
 * @param {Object[]} communities
 * @returns {string}
 */
const resolveCommunityIdForAnnouncement = (announcement, communities) => {
  if (announcement.communityId) {
    return announcement.communityId;
  }
  if (announcement.podId) {
    const podCommunity = communities.find(
      (item) => item.type === CommunityType.POD && item.podId === announcement.podId,
    );
    return podCommunity ? podCommunity.id : ONE_ON_ONE_ID;
  }
  return isGenericCommunityId(announcement.tagId) ? announcement.tagId : DEFAULT_COMMUNITY_ID;
};

/**
 * The community-targeting fields a new post doc writes, dropping the legacy tag/pod keys when the
 * community has no equivalent rather than storing nulls.
 * @param {Object} selection - Output of `buildPostFieldsForCommunity`.
 * @returns {Object}
 */
const buildCreateCommunityFields = (selection) => ({
  communityId: selection.communityId,
  communityName: selection.communityName,
  ...(selection.tagName !== null ? { tagId: selection.tagId, tagName: selection.tagName } : {}),
  ...(selection.podId !== null ? { podId: selection.podId } : {}),
});

/**
 * Payload for a sibling joining an existing group, i.e. a community added while editing a post.
 *
 * Author fields are cloned from an existing sibling rather than taken from the logged-in user, so
 * an assistant coach adding a community to someone else's post does not reattribute it.
 *
 * @param {Object} params
 * @param {Object} params.selection - Output of `buildPostFieldsForCommunity` for the new community.
 * @param {Object} params.base - An existing sibling to clone author fields from.
 * @param {string} params.message
 * @param {Object[]} params.attachments
 * @param {string} params.basePostId
 * @param {string[]} params.groupCommunityIds
 * @param {string} params.loggedInUserId
 * @param {Object} [params.schedule] - `{ isScheduling, publishAt, scheduledStatus }` when the post
 * is still pending publication.
 * @param {Date} [params.createdAt] - The group's shared creation time, so siblings stay adjacent in
 * every feed ordering. Defaults to the base sibling's, but a save that publishes a pending post
 * must pass the same fresh timestamp its siblings are being moved to. No `originalCreatedAt` goes
 * with it: that field records a `createdAt` being overwritten, and this doc has none to preserve.
 * @returns {Object}
 */
const buildNewSiblingData = ({
  selection,
  base,
  message,
  attachments,
  basePostId,
  groupCommunityIds,
  loggedInUserId,
  schedule = {},
  createdAt = base.createdAt,
}) => ({
  message,
  coach: base.coach,
  createdAt,
  createdBy: base.createdBy,
  likes: 0,
  attachments,
  isByCoach: base.isByCoach,
  postAvatarUrl: base.postAvatarUrl,
  postUserName: base.postUserName,
  basePostId,
  groupCommunityIds,
  lastUpdatedAt: new Date(),
  lastUpdatedBy: loggedInUserId,
  isRemoved: !!schedule.isScheduling,
  ...(schedule.isScheduling && schedule.publishAt ? {
    publishAt: schedule.publishAt,
    scheduledStatus: schedule.scheduledStatus,
  } : {}),
  ...buildCreateCommunityFields(selection),
});

export {
  processAttachments,
  onFilesSelected,
  DELIVERY_MODE,
  ONE_ON_ONE_ID,
  DEFAULT_COMMUNITY_ID,
  buildPostFieldsForCommunity,
  resolveCommunityIdForAnnouncement,
  buildCreateCommunityFields,
  buildNewSiblingData,
};
